import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  DoughBatchIngredient,
  DoughBatchIngredientsState,
  CreateDoughBatchIngredientRequest,
  UpdateDoughBatchIngredientRequest
} from '../../types/doughBatchIngredient';
import { DoughProductName } from '../../types/doughBatch';
import { doughBatchIngredientApiService } from '../../services/doughBatchIngredientApiService';

// Async thunks

/**
 * Fetch all dough batch ingredients from the API
 */
export const fetchAllDoughBatchIngredients = createAsyncThunk(
  'doughBatchIngredients/fetchAll',
  async () => {
    return await doughBatchIngredientApiService.fetchAllDoughBatchIngredients();
  }
);

/**
 * Fetch dough batch ingredients for a specific dough batch
 */
export const fetchDoughBatchIngredientsByDoughBatchId = createAsyncThunk(
  'doughBatchIngredients/fetchByDoughBatchId',
  async (doughBatchId: string) => {
    return await doughBatchIngredientApiService.fetchDoughBatchIngredientsByDoughBatchId(doughBatchId);
  }
);

/**
 * Get default ingredients for a product type
 */
export const getDefaultIngredients = createAsyncThunk(
  'doughBatchIngredients/getDefaultIngredients',
  async (productName: DoughProductName) => {
    return await doughBatchIngredientApiService.getDefaultIngredients(productName);
  }
);

/**
 * Create a new dough batch ingredient
 */
export const createDoughBatchIngredient = createAsyncThunk(
  'doughBatchIngredients/create',
  async (ingredient: CreateDoughBatchIngredientRequest) => {
    return await doughBatchIngredientApiService.createDoughBatchIngredient(ingredient);
  }
);

/**
 * Update an existing dough batch ingredient
 */
export const updateDoughBatchIngredient = createAsyncThunk(
  'doughBatchIngredients/update',
  async ({ id, data }: { id: string; data: UpdateDoughBatchIngredientRequest }) => {
    return await doughBatchIngredientApiService.updateDoughBatchIngredient(id, data);
  }
);

/**
 * Delete a dough batch ingredient
 */
export const deleteDoughBatchIngredient = createAsyncThunk(
  'doughBatchIngredients/delete',
  async (id: string) => {
    await doughBatchIngredientApiService.deleteDoughBatchIngredient(id);
    return id;
  }
);

// Initial state
const initialState: DoughBatchIngredientsState = {
  ingredients: [],
  loading: false,
  error: null,
};

const doughBatchIngredientSlice = createSlice({
  name: 'doughBatchIngredients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Add ingredients to state (useful when batch creation returns ingredients)
    addIngredients: (state, action) => {
      const newIngredients = action.payload as DoughBatchIngredient[];
      // Add only ingredients that don't already exist
      newIngredients.forEach(ing => {
        if (!state.ingredients.find(i => i.id === ing.id)) {
          state.ingredients.push(ing);
        }
      });
    }
  },
  extraReducers: (builder) => {
    // Fetch all dough batch ingredients
    builder
      .addCase(fetchAllDoughBatchIngredients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllDoughBatchIngredients.fulfilled, (state, action) => {
        state.loading = false;
        state.ingredients = action.payload;
      })
      .addCase(fetchAllDoughBatchIngredients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dough batch ingredients';
      });

    // Fetch by dough batch ID
    builder
      .addCase(fetchDoughBatchIngredientsByDoughBatchId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoughBatchIngredientsByDoughBatchId.fulfilled, (state, action) => {
        state.loading = false;
        // Replace ingredients for this dough batch
        const doughBatchId = action.payload[0]?.doughBatchId;
        if (doughBatchId) {
          state.ingredients = [
            ...state.ingredients.filter(i => i.doughBatchId !== doughBatchId),
            ...action.payload
          ];
        } else if (action.payload.length === 0) {
          // Empty result - need to get doughBatchId from the thunk argument
          // The ingredients array is empty, so no action needed
        }
      })
      .addCase(fetchDoughBatchIngredientsByDoughBatchId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dough batch ingredients';
      });

    // Create dough batch ingredient
    builder
      .addCase(createDoughBatchIngredient.fulfilled, (state, action) => {
        state.ingredients.push(action.payload);
      })
      .addCase(createDoughBatchIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create dough batch ingredient';
      });

    // Update dough batch ingredient
    builder
      .addCase(updateDoughBatchIngredient.fulfilled, (state, action) => {
        const index = state.ingredients.findIndex(i => i.id === action.payload.id);
        if (index !== -1) {
          state.ingredients[index] = action.payload;
        }
      })
      .addCase(updateDoughBatchIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update dough batch ingredient';
      });

    // Delete dough batch ingredient
    builder
      .addCase(deleteDoughBatchIngredient.fulfilled, (state, action) => {
        state.ingredients = state.ingredients.filter(i => i.id !== action.payload);
      })
      .addCase(deleteDoughBatchIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete dough batch ingredient';
      });
  }
});

export const { clearError, addIngredients } = doughBatchIngredientSlice.actions;
export default doughBatchIngredientSlice.reducer;
